import _ from 'underscore';
import Joi from 'joi';
import {
	api as apiRequest,
	batchApi as batchApiRequest
} from '@culture/common-helpers/request';
import { addCleanCache } from '@culture/common-helpers/params';
import {
	checkEntityStatus,
	getFullEntityUrl
} from '@culture/common-helpers/entities';
import { getEntityTags } from '@culture/common-helpers/tags';

import Template from 'pageTemplates/entities/news';
import { getPushkinDayPromoBlock } from 'utils/helpers/pushkinDayPromoBlock';

const getPublication = ({ _id, cleanCache }) => {
	const params = addCleanCache(
		{
			fields: [
				'_id',
				'jsonText',
				'mainRubric',
				'name',
				'rubrics',
				'tags',
				'text',
				'title',
				'type',
				'status',
				'thumbnailFile',
				'similarEntities',
				'podcasts'
			].join()
		},
		cleanCache
	);

	return apiRequest(`publications/${_id}`, params);
};

const getParentRubric = ({ publication, cleanCache }) => {
	const { mainRubric } = publication;

	if (mainRubric?._id) {
		const params = addCleanCache({ fields: ['parentId'].join() }, cleanCache);

		return apiRequest(`rubrics/${mainRubric._id}`, params);
	}

	return null;
};

const getSimilarMaterials = (
	{ _id, cleanCache },
	{ type, rubrics, parentRubric }
) => {
	const childrenValue = rubrics[0]?._id;
	const parentValue = parentRubric && parentRubric[0]?.parentId;

	const params = addCleanCache(
		{
			fields: [
				'_id',
				'name',
				'podcasts',
				'thumbnailFile',
				'title',
				'type'
			].join(),
			entityType: type,
			entityId: _id,
			rubricId: childrenValue && (parentValue || rubrics[0]?._id),
			limit: 10,
			cached: true
		},
		cleanCache
	);

	return apiRequest('entities/similar', params);
};

const getBreadcrumbs = ({ _id, cleanCache }) => {
	const params = addCleanCache(
		{
			_id,
			entityType: 'publications',
			cached: true
		},
		cleanCache
	);

	return apiRequest('breadcrumbs', params);
};

const getEntityMeta = ({ _id, cleanCache }) => {
	const params = addCleanCache(
		{
			_id,
			entityType: 'publications',
			cached: true
		},
		cleanCache
	);

	return apiRequest('meta/entity', params);
};

export async function getServerSideProps({ query, req }) {
	const validationSchema = Joi.object().keys({
		_id: Joi.number().integer().min(1).required(),
		name: Joi.string(),
		cleanCache: Joi.boolean().failover(false)
	});

	const params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	const publication = await getPublication(params);

	if (_.isEmpty(publication) || !checkEntityStatus(publication)) {
		return {
			notFound: true
		};
	}

	if (!params.name || publication.name !== params.name) {
		return {
			redirect: {
				destination: getFullEntityUrl(req.url, 'publications', publication),
				permanent: true
			}
		};
	}

	const parentRubric =
		(await getParentRubric({
			publication,
			cleanCache: params.cleanCache
		})) || [];

	const requests = [
		getSimilarMaterials(params, { ...publication, parentRubric }),
		getBreadcrumbs(params),
		getEntityMeta(params),
		getPushkinDayPromoBlock()
	];

	const [
		similarMaterials,
		breadcrumbs,
		entityMeta,
		pushkinDayPromoBlock
	] = await batchApiRequest(requests);

	const flattenedSimilarMaterials = similarMaterials.map(
		(similarTheme) => similarTheme.entity
	);

	return {
		props: {
			breadcrumbs,
			entityMeta,
			publication,
			similarMaterials: flattenedSimilarMaterials.slice(-10),
			tags: getEntityTags(publication),
			pushkinDayPromoBlock: _(pushkinDayPromoBlock).pick('shortText', 'text'),
			template: 'detail'
		}
	};
}

export default Template;
